import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { Alert, Keyboard, TextInput, View } from 'react-native';
import Modal from 'utils/packages/Modal';
import { AppView, rem } from 'design-system';
import { KeyboardAvoidingContainerNew } from 'design-system/KeyboardAvoidingContainerNew';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import StatusBar from 'design-system/StatusBar';
import { styles as textStyles } from 'design-system/Text';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { muteErrorAndResultButThrows, useFetchOld } from 'hooks';
import { getSocialMediaData } from 'actions/socialMedia';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useAppSelector } from 'store/hooks';
import { getWebsiteLogo } from 'actions/websiteLogo';
import { extractDomainFromInput, normalizeUsernameInput } from './util';
import { makeSelectTransactionById, selectHasSuggestInstagramMerchantFeature, } from '../../selectors';
const strings = {
    title: {
        TWITTER: 'X profile',
        INSTAGRAM: 'Instagram profile',
        WEB_DOMAIN: 'Website',
    },
    heading: {
        TWITTER: 'Tell us their X handle',
        INSTAGRAM: 'You can add their profile picture by telling us their Instagram handle if they have business or creator account',
        WEB_DOMAIN: 'You can add their favicon image by telling us their website URL',
    },
    placeholder: {
        TWITTER: '@X_handle',
        INSTAGRAM: '@Instagram_handle',
        WEB_DOMAIN: 'https://',
    },
    alertTitle: 'Hold on 💫',
    alertMessageInvalidUrl: 'You must enter a valid URL or Web domain',
    next: 'Continue',
    accountNotFound: 'Account not found',
    accountNotFoundMessage: 'We couldn’t find an account matching the handle you entered. Please double-check for any typos and try again',
    websiteLogoNotFound: 'Logo not found',
    websiteLogoNotFoundMessage: 'We couldn’t get a logo from the URL you entered. This usually happens if the URL is incorrect, the website doesn’t have a logo, or there’s a temporary connection issue. Please double-check for any typos and try again.',
};
const INPUT_MAX_LEN = {
    TWITTER: 1 + 15,
    INSTAGRAM: 1 + 30,
    WEB_DOMAIN: 2000,
};
const MerchantFeedbackScreen = ({ navigation, route, }) => {
    const { transactionId, keyBack, suggestType = 'TWITTER', } = route.params ?? {};
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: strings.title[suggestType],
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, suggestType]);
    const selectTransactionById = useMemo(makeSelectTransactionById, []);
    const transaction = useAppSelector((store) => selectTransactionById(store, transactionId));
    const hasSuggestInstagramMerchantFeature = useAppSelector(selectHasSuggestInstagramMerchantFeature);
    useEffect(() => {
        if (!transaction) {
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            else {
                navigation.navigate('Feed');
            }
        }
    }, [transaction]);
    const [inputValue, setInputValue] = useState('');
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResultButThrows);
    const showErrorModal = useCallback(() => {
        Modal.showError(suggestType === 'WEB_DOMAIN'
            ? strings.websiteLogoNotFoundMessage
            : strings.accountNotFoundMessage, undefined, suggestType === 'WEB_DOMAIN'
            ? strings.websiteLogoNotFound
            : strings.accountNotFound);
    }, [suggestType]);
    const onPressNext = useCallback(() => {
        if (!inputValue || inputValue.length <= 1) {
            return; // unreachable as button is disabled
        }
        Keyboard.dismiss();
        if (!hasSuggestInstagramMerchantFeature && suggestType === 'INSTAGRAM') {
            return; // do nothing
        }
        if (suggestType === 'TWITTER' || suggestType === 'INSTAGRAM') {
            fetch(getSocialMediaData(suggestType, inputValue))
                .then((data) => {
                if (data) {
                    navigation.navigate('ConfirmMerchant', {
                        keyBack,
                        transactionId,
                        merchantData: data,
                    });
                }
                else {
                    // show modal
                    showErrorModal();
                }
            })
                .catch(showErrorModal);
        }
        else {
            const domain = extractDomainFromInput(inputValue);
            if (!domain) {
                Alert.alert(strings.alertTitle, strings.alertMessageInvalidUrl);
                return;
            }
            fetch(getWebsiteLogo(domain))
                .then((data) => {
                if (data) {
                    navigation.navigate('ConfirmMerchant', {
                        keyBack,
                        transactionId,
                        merchantData: {
                            type: suggestType,
                            handle: data.domain,
                            name: data.domain,
                            iconUrl: data.iconUrl,
                        },
                    });
                }
                else {
                    showErrorModal();
                }
            })
                .catch(showErrorModal);
        }
    }, [
        hasSuggestInstagramMerchantFeature,
        inputValue,
        keyBack,
        showErrorModal,
        suggestType,
        transactionId,
    ]);
    const onChangeText = useCallback((data) => {
        if (suggestType === 'WEB_DOMAIN') {
            setInputValue(data);
        }
        else {
            if (!data) {
                setInputValue('@');
                return;
            }
            const normalized = normalizeUsernameInput(data);
            if (normalized) {
                setInputValue(normalized);
            }
        }
    }, [suggestType]);
    const textInputRef = useRef(null);
    useAutoFocusInput(textInputRef);
    const styles = useStyles(styleSet);
    const isSameAsBefore = useMemo(() => {
        if (!transaction?.suggestedData)
            return false;
        if (inputValue.length <= 1)
            return false;
        const { type, handle } = transaction.suggestedData;
        if (suggestType !== type)
            return false;
        const normalize = type === 'WEB_DOMAIN' ? extractDomainFromInput : normalizeUsernameInput;
        return normalize(inputValue) === normalize(handle);
    }, [inputValue, suggestType, transaction?.suggestedData]);
    const fontStyle = useMemo(() => {
        if (inputValue.length >= 36)
            return textStyles.TextThin_16;
        if (inputValue.length >= 20)
            return textStyles.TextThin_20;
        return textStyles.TextThin_24;
    }, [inputValue.length]);
    return (<AppView type="primary" avoidHeader>
      <StatusBar animated barStyle="dark-content"/>
      <KeyboardAvoidingContainerNew flexContainer withButtonSpace>
        <ScreenInfoHeader subTitle={strings.heading[suggestType]} style={styles.heading}/>
        <View style={styles.container}>
          <TextInput ref={textInputRef} placeholder={strings.placeholder[suggestType]} autoCorrect={false} autoCapitalize={'none'} allowFontScaling={false} value={inputValue} onChangeText={onChangeText} placeholderTextColor={colors.text.placeholder} maxLength={INPUT_MAX_LEN[suggestType]} selectionColor={colors.text.selection} underlineColorAndroid="transparent" style={[styles.text, fontStyle]} returnKeyType="done" onSubmitEditing={onPressNext} keyboardType={suggestType === 'WEB_DOMAIN' ? 'url' : undefined} testID="merchantInput"/>
        </View>
      </KeyboardAvoidingContainerNew>
      <KeyboardAvoidingButton brand onPress={onPressNext} title={strings.next} withFrame={false} testID="nextButton" isFetching={isFetching} disabled={!inputValue ||
            inputValue.length <= 1 ||
            isSameAsBefore ||
            (!hasSuggestInstagramMerchantFeature && suggestType === 'INSTAGRAM')}/>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    heading: {
        paddingHorizontal: 0,
    },
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        paddingHorizontal: rem(16),
    },
    text: {
        height: '100%',
        width: '100%',
        fontFamily: 'Montserrat-Light',
        color: colors.elements.primary,
        textAlignVertical: 'center',
        textAlign: 'center',
        includeFontPadding: false,
        padding: 0,
        margin: 0,
        borderWidth: 0,
    },
}));
export default MerchantFeedbackScreen;
